// features/tutorials/components/CompletionCelebration.tsx
import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import confetti from 'canvas-confetti';

interface CompletionCelebrationProps {
    message: string;
    submessage: string;
}

export const CompletionCelebration: React.FC<CompletionCelebrationProps> = ({
    message,
    submessage
}) => {
    const hasFired = useRef(false);

    useEffect(() => {
        if (!hasFired.current) {
            hasFired.current = true;

            const duration = 3000;
            const animationEnd = Date.now() + duration;

            const interval: NodeJS.Timeout = setInterval(() => {
                const timeLeft = animationEnd - Date.now();

                if (timeLeft <= 0) {
                    return clearInterval(interval);
                }

                const particleCount = 50 * (timeLeft / duration);

                // Multiple bursts from different positions
                confetti({
                    particleCount: Math.floor(particleCount),
                    spread: 60,
                    origin: { x: 0.2, y: 0.7 },
                    colors: ['#FFD700', '#FFA500', '#FF6347', '#FF69B4', '#00CED1'],
                });

                confetti({
                    particleCount: Math.floor(particleCount),
                    spread: 60,
                    origin: { x: 0.8, y: 0.7 },
                    colors: ['#FFD700', '#FFA500', '#FF6347', '#FF69B4', '#00CED1'],
                });

                confetti({
                    particleCount: Math.floor(particleCount / 2),
                    spread: 100,
                    origin: { x: 0.5, y: 0.5 },
                    colors: ['#FFD700', '#FFA500'],
                });
            }, 250);

            return () => clearInterval(interval);
        }
    }, []);

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none"
        >
            <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: [0, 1.2, 1] }}
                transition={{ duration: 0.5 }}
                className="text-center"
            >
                <motion.div
                    animate={{ y: [0, -20, 0] }}
                    transition={{ duration: 0.5, repeat: 3 }}
                    className="text-6xl mb-4"
                >
                    🎉
                </motion.div>
                <h2 className="text-4xl font-bold text-slate-900 dark:text-white mb-2">
                    {message}
                </h2>
                <p className="text-xl text-slate-600 dark:text-slate-400">
                    {submessage}
                </p>
            </motion.div>
        </motion.div>
    );
};